import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { LockIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import { useCallback } from 'react';

import * as styles from './feature-upgrade-nudge.css';

export interface FeatureUpgradeNudgeProps {
  /** Feature title text (caller resolves via useI18n). */
  featureName: string;
  /** Name of the lowest plan with the feature; null when no plan has it. */
  requiredPlan: string | null;
  /** Optional CTA override. Defaults to generic "Upgrade plan" i18n. */
  ctaText?: string;
  /**
   * Custom upgrade handler. Defaults to navigating to workspace:plans tab.
   * `null` hides the CTA for viewers who cannot manage plans.
   */
  onUpgrade?: (() => void) | null;
  className?: string;
  testId?: string;
  /** Optional feature key — reserved for future analytics/telemetry. */
  featureKey?: string;
}

const navigateToPlansTab = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', 'workspace:plans');
  window.location.href = url.toString();
};

export const FeatureUpgradeNudge = ({
  featureName,
  requiredPlan,
  ctaText,
  onUpgrade,
  className,
  testId,
}: FeatureUpgradeNudgeProps) => {
  const t = useI18n();

  const handleClick = useCallback(() => {
    if (onUpgrade === null) return;
    (onUpgrade ?? navigateToPlansTab)();
  }, [onUpgrade]);

  return (
    <div className={clsx(styles.root, className)} data-testid={testId}>
      <LockIcon className={styles.icon} />
      <h3 className={styles.title}>{featureName}</h3>
      <p className={styles.description}>
        {requiredPlan
          ? t['com.streamlain.feature-gate.upgrade-description']({
              plan: requiredPlan,
            })
          : t['com.streamlain.feature-gate.not-in-plan']()}
      </p>
      {onUpgrade === null ? null : (
        <Button variant="primary" onClick={handleClick}>
          {ctaText ?? t['com.streamlain.feature-gate.upgrade-cta']()}
        </Button>
      )}
    </div>
  );
};
